import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, ChevronRight, ChevronLeft, X, Check, Target, Utensils, Syringe } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

const TEAL = "#0D9488";

/** 90-second walkthrough for brand-new users.
 *  Auto-opens on Dashboard when GET /client/onboarding-status.onboarded is false.
 *  Steps: (1) Set goal + calorie/protein  (2) CTA to log first shot  (3) CTA to log first meal.
 */
export default function ClientOnboardingWizard({ open, onClose, initial }) {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    goal_weight: initial?.goal_weight || "",
    calorie_goal: initial?.calorie_goal || 2200,
    protein_goal: initial?.protein_goal || 150,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setStep(0); }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") finish(true); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const finish = async (skipped = false) => {
    setBusy(true);
    try {
      const payload = {};
      if (form.goal_weight) payload.goal_weight = parseFloat(form.goal_weight);
      if (form.calorie_goal) payload.calorie_goal = parseInt(form.calorie_goal, 10);
      if (form.protein_goal) payload.protein_goal = parseInt(form.protein_goal, 10);
      await api.post("/client/onboarded", payload);
      if (!skipped) toast.success("You're all set!");
      onClose?.(true);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const jumpToShot = async () => { await finish(true); nav("/shots"); };
  const jumpToMeal = async () => { await finish(true); nav("/nutrition"); };

  const steps = [
    {
      title: "Set your day-1 goals",
      body: "You can change these any time from Profile. This just wires up the streak & calorie widgets so the dashboard feels alive from the jump.",
      render: () => (
        <div className="space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1 flex items-center gap-1.5">
              <Target className="w-3 h-3" /> Goal weight (lb)
            </label>
            <Input type="number" min={40} max={800} step="0.5"
                   value={form.goal_weight}
                   onChange={(e) => setForm((f) => ({ ...f, goal_weight: e.target.value }))}
                   placeholder="e.g. 175"
                   className="bg-white border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488] font-mono-num"
                   data-testid="onboard-goal-weight" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">Calorie goal</label>
              <Input type="number" min={800} max={8000} step="10"
                     value={form.calorie_goal}
                     onChange={(e) => setForm((f) => ({ ...f, calorie_goal: e.target.value }))}
                     className="bg-white border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488] font-mono-num"
                     data-testid="onboard-calorie" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">Protein goal (g)</label>
              <Input type="number" min={20} max={500} step="5"
                     value={form.protein_goal}
                     onChange={(e) => setForm((f) => ({ ...f, protein_goal: e.target.value }))}
                     className="bg-white border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488] font-mono-num"
                     data-testid="onboard-protein" />
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Log your first shot",
      body: "Fastest way to feel the platform click — log the peptide, dose, and site once and you'll see a live countdown on the dashboard.",
      render: () => (
        <div className="flex flex-col items-center py-4 gap-4">
          <div className="w-14 h-14 rounded-full bg-teal-50 flex items-center justify-center">
            <Syringe className="w-7 h-7 text-teal-700" />
          </div>
          <p className="text-sm text-slate-600 text-center leading-relaxed max-w-sm">
            We'll drop you on the Shots page. Fill in your peptide + dose and the dashboard countdown timer flips on the moment you save.
          </p>
          <Button type="button" onClick={jumpToShot} disabled={busy}
                  className="text-white" style={{ background: TEAL }}
                  data-testid="onboard-jump-shot">
            <Syringe className="w-3.5 h-3.5 mr-1.5" /> Log my first shot
          </Button>
        </div>
      ),
    },
    {
      title: "Log your first meal",
      body: "Type or dictate what you just ate — the AI parses macros in ~2 seconds and the calorie ring lights up.",
      render: () => (
        <div className="flex flex-col items-center py-4 gap-4">
          <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center">
            <Utensils className="w-7 h-7 text-amber-600" />
          </div>
          <p className="text-sm text-slate-600 text-center leading-relaxed max-w-sm">
            We'll drop you on the Nutrition page. Try the mic button and speak your last meal — takes about five seconds.
          </p>
          <Button type="button" onClick={jumpToMeal} disabled={busy}
                  className="text-white" style={{ background: TEAL }}
                  data-testid="onboard-jump-meal">
            <Utensils className="w-3.5 h-3.5 mr-1.5" /> Log my first meal
          </Button>
        </div>
      ),
    },
  ];

  const active = steps[step];
  const next = () => (step < steps.length - 1 ? setStep((s) => s + 1) : finish(false));
  const back = () => setStep((s) => Math.max(0, s - 1));

  return createPortal(
    <div
      className="fixed inset-0 z-[9990] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="client-wizard-overlay"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        data-testid="client-wizard-panel"
      >
        <div className="px-6 pt-5 pb-3 flex items-center justify-between border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#0D9488]" />
            <span className="text-[11px] uppercase tracking-widest font-semibold text-[#0D9488]">
              Welcome · {step + 1} / {steps.length}
            </span>
          </div>
          <button type="button" onClick={() => finish(true)}
                  className="text-slate-400 hover:text-slate-700"
                  data-testid="client-wizard-close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="h-1 bg-slate-50 relative overflow-hidden">
          <div className="absolute inset-y-0 left-0 bg-[#0D9488] transition-all duration-300"
               style={{ width: `${((step + 1) / steps.length) * 100}%` }} />
        </div>
        <div className="px-6 py-5">
          <h2 className="font-display font-bold text-2xl text-slate-900 mb-1" data-testid="client-wizard-title">{active.title}</h2>
          <p className="text-sm text-slate-500 mb-5 leading-relaxed">{active.body}</p>
          {active.render()}
        </div>
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/60 flex items-center justify-between">
          <button type="button" onClick={() => finish(true)}
                  className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 hover:text-slate-800"
                  data-testid="client-wizard-skip">Skip for now</button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <Button type="button" variant="ghost" size="sm" onClick={back}
                      className="text-slate-700 hover:bg-slate-100"
                      data-testid="client-wizard-back">
                <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Back
              </Button>
            )}
            <Button type="button" size="sm" onClick={next} disabled={busy}
                    className="text-white" style={{ background: TEAL }}
                    data-testid="client-wizard-next">
              {step < steps.length - 1
                ? <>Next <ChevronRight className="w-3.5 h-3.5 ml-1" /></>
                : <><Check className="w-3.5 h-3.5 mr-1" /> Finish</>}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
